import { Association } from "@entities";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SmsIcon from "@mui/icons-material/Sms";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Message01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { CircularProgress, Stack } from "@mui/material";
import { associationRepo, opportunityRepo } from "@repositories";
import { OrganizationRoles } from "@shared/Roles";
import useCreateHyperTable from "@shared/frontend/hyper-table";
import type { ActiveFilter } from "@shared/frontend/hyper-table/filter-types";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { linko } from "@shared/linko";
import { isAxiosTypedLinkoError } from "@shared/exceptions-message";
import { LATEST_COMMUNICATION_RECORDS_INCLUDE, ADMIN_BULK_ACTION_LIMIT } from "../../../helpers/admin-list-query";
import { UserCardPopup } from "clients/admin/src/desktop/components/popups/user-card/UserCardPopup";
import { useOrganization } from "clients/admin/src/desktop/contexts/OrganizationProvider";
import { useUser } from "clients/admin/src/desktop/contexts/UserProvider";
import { useAlert } from "clients/admin/src/desktop/contexts/AlertProvider";
import { useAddUserToOpportunity } from "../../../queries/events.query";
import {
  MatchWeightPromptCancelled,
  resolveMatchWeightForAdd,
  usePromptMatchWeight,
} from "../../../dialog-components/MatchWeightDialog";
import { useVolunteerColumns } from "../users-table/volunteers/columns";
import { getFilters } from "../users-table/volunteers/filters";
import { RelevantOpportunitiesForVolunteerDialog } from "./RelevantOpportunitiesForVolunteerDialog";
import { hasUsableVolunteerCoordinates } from "./RelevantOpportunitiesForVolunteerList";
import {
  SendEventParticipantsWhatsappDialog,
  type WhatsappDialogRecipient,
} from "../opprtunity-participants/SendEventParticipantsWhatsappDialog";
import { RelevantVolunteersDistanceColumn } from "./RelevantVolunteersDistanceColumn";
import { splitVolunteerColumnsForDistance } from "./relevant-volunteers-distance";
import {
  mapActiveFiltersToRankedFilters,
  rankedFiltersCacheKey,
  type RankedVolunteersTableFilters,
} from "./relevant-volunteers-table-filters";

const PAGE_SIZE = 20;
/** Only used when "select all" runs an action - not for table display. */
const RANKED_ACTION_CHUNK = 2000;

type RankedUsersPayload =
  | {
    items?: Array<{
      id: string;
      distanceFromLocationKM?: number | null;
    }>;
    totalCount?: number;
  }
  | Array<{ id: string; distanceFromLocationKM?: number | null; }>
  | null
  | undefined;

function parseRankedPayload(ranked: RankedUsersPayload) {
  const rankedList = Array.isArray(ranked) ? ranked : (ranked?.items ?? []);
  const totalCount = Array.isArray(ranked)
    ? rankedList.length
    : Number(ranked?.totalCount ?? rankedList.length);
  return { rankedList, totalCount };
}

async function fetchRankedUsersPage(params: {
  opportunityId: string;
  limit: number;
  offset: number;
  shuffleRemoteVolunteers?: boolean;
  filters?: RankedVolunteersTableFilters;
}) {
  const { data: ranked } =
    await linko.opportunity.queries.getRankedUsersForOpportunity({
      opportunityId: params.opportunityId,
      limit: params.limit,
      offset: params.offset,
      ...(params.filters ? { filters: params.filters } : {}),
      ...(params.shuffleRemoteVolunteers
        ? { shuffleRemoteVolunteers: true }
        : {}),
    } as Parameters<
      typeof linko.opportunity.queries.getRankedUsersForOpportunity
    >[0]);

  return parseRankedPayload(ranked as RankedUsersPayload);
}

/** Loads every ranked user id - only for select-all actions. */
async function fetchAllRankedUserIds(
  opportunityId: string,
  shuffleRemoteVolunteers?: boolean,
  filters?: RankedVolunteersTableFilters,
): Promise<string[]> {
  const userIds: string[] = [];
  let offset = 0;
  let totalCount = Infinity;

  while (offset < totalCount) {
    const { rankedList, totalCount: pageTotal } = await fetchRankedUsersPage({
      opportunityId,
      limit: RANKED_ACTION_CHUNK,
      offset,
      shuffleRemoteVolunteers,
      filters,
    });
    totalCount = pageTotal;
    for (const row of rankedList) userIds.push(row.id);
    if (rankedList.length === 0) break;
    offset += RANKED_ACTION_CHUNK;
  }

  return userIds;
}

const ASSOCIATION_INCLUDE = {
  user: { include: { skills: { include: { skill: true } } } },
  teams: { include: { team: true } },
  status: true,
  ...LATEST_COMMUNICATION_RECORDS_INCLUDE,
} as const;

type Props = {
  opportunityId: string;
  /** Already on the volunteers-only page - hide "open in new tab". */
  standalone?: boolean;
  previewRows?: Association[];
  onSendSuccess?: (result: {
    sent: number;
    failed: number;
    skipped?: number;
    message: string;
  }) => void;
  onAddSuccess?: () => void;
};

export function RelevantVolunteersPanel({
  opportunityId,
  standalone = false,
  previewRows,
  onSendSuccess,
  onAddSuccess,
}: Props) {
  const { currOrganization } = useOrganization();
  const { currentUser } = useUser();
  const isCallCenterStation = !!currentUser?.isCallCenterStation;
  const { setAlertText, setSeverity } = useAlert();
  const queryClient = useQueryClient();
  const HyperTable = useCreateHyperTable(Association);
  const { mutateAsync: addUserToOpportunity } = useAddUserToOpportunity();
  const promptMatchWeight = usePromptMatchWeight();
  const [cardAssociation, setCardAssociation] = useState<Association>();
  const [calledAssociationId, setCalledAssociationId] = useState<string | null>(
    null,
  );
  const [opportunitiesDialogVolunteer, setOpportunitiesDialogVolunteer] =
    useState<{
      userId: string;
      userName: string;
      associationId: string;
      hasVolunteerCoordinates: boolean;
    } | null>(null);
  const [whatsappRecipients, setWhatsappRecipients] = useState<
    WhatsappDialogRecipient[]
  >([]);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [distanceByUserId, setDistanceByUserId] = useState(
    () => new Map<string, number | null>(),
  );

  useEffect(() => {
    if (!previewRows) return;
    const next = new Map<string, number | null>();
    for (const row of previewRows) {
      const km = (row as Association & { distanceKm?: number }).distanceKm;
      if (row.userId) next.set(row.userId, km ?? null);
    }
    setDistanceByUserId(next);
  }, [previewRows]);
  const distanceByUserIdRef = useRef(distanceByUserId);
  const [activeFilters, setActiveFilters] = useState<
    ActiveFilter<Association>[]
  >([]);
  const activeFiltersRef = useRef<ActiveFilter<Association>[]>([]);
  const [filtersSynced, setFiltersSynced] = useState(false);

  const handleFiltersChange = useCallback(
    (filters: ActiveFilter<Association>[]) => {
      activeFiltersRef.current = filters;
      setActiveFilters(filters);
      setFiltersSynced(true);
    },
    [],
  );

  const rankedTableFilters = useMemo(
    () => mapActiveFiltersToRankedFilters(activeFilters),
    [activeFilters],
  );
  const rankedFiltersKey = useMemo(
    () => rankedFiltersCacheKey(rankedTableFilters),
    [rankedTableFilters],
  );

  const { data: opportunityMeta, isFetched: isOpportunityMetaFetched } =
    useQuery({
      queryKey: ["relevant-volunteers-opportunity-meta", opportunityId],
      queryFn: () =>
        opportunityRepo.findOne({
          where: { id: opportunityId },
          select: { id: true, isRemote: true, defaultMatchWeight: true, title: true, startDate: true, location: true, timeDescription: true, hasTime: true },
        }),
      enabled: !!opportunityId && !previewRows,
    });
  const isRemoteOpportunity = opportunityMeta?.isRemote === true;

  const baseAssociationWhere = useMemo(() => {
    if (!currOrganization) return {};
    return Association.hyperFilter({
      organizationId: currOrganization.id,
      roles: {
        $some: OrganizationRoles.Volunteer,
      },
      user: {
        $is: {
          isDeleted: false,
        },
      },
    });
  }, [currOrganization]);

  const hydrateAssociationsInRankOrder = useCallback(
    async (userIds: string[]) => {
      if (!currOrganization || userIds.length === 0) return [] as Association[];

      const associations = await associationRepo.find({
        where: Association.hyperFilter({
          organizationId: currOrganization.id,
          userId: { $in: userIds },
          roles: {
            $some: OrganizationRoles.Volunteer,
          },
          user: {
            $is: {
              isDeleted: false,
            },
          },
        }),
        include: ASSOCIATION_INCLUDE,
      });

      const byUserId = new Map(
        associations.map((association) => [association.userId, association]),
      );
      return userIds
        .map((id) => byUserId.get(id))
        .filter((row): row is Association => !!row);
    },
    [currOrganization],
  );

  const rankedDataFn = useCallback(
    async ({ page, limit }: { page: number; limit: number; }) => {
      const filters = mapActiveFiltersToRankedFilters(activeFiltersRef.current);
      const { rankedList } = await fetchRankedUsersPage({
        opportunityId,
        limit,
        offset: page * limit,
        shuffleRemoteVolunteers: isRemoteOpportunity,
        filters,
      });

      const nextDistances = new Map(distanceByUserIdRef.current);
      for (const row of rankedList) {
        nextDistances.set(row.id, row.distanceFromLocationKM ?? null);
      }
      distanceByUserIdRef.current = nextDistances;
      setDistanceByUserId(new Map(nextDistances));

      return hydrateAssociationsInRankOrder(rankedList.map((row) => row.id));
    },
    [
      opportunityId,
      isRemoteOpportunity,
      hydrateAssociationsInRankOrder,
    ],
  );

  const rankedCountFn = useCallback(async () => {
    const filters = mapActiveFiltersToRankedFilters(activeFiltersRef.current);
    const { totalCount } = await fetchRankedUsersPage({
      opportunityId,
      limit: 1,
      offset: 0,
      shuffleRemoteVolunteers: isRemoteOpportunity,
      filters,
    });
    return totalCount;
  }, [opportunityId, isRemoteOpportunity]);

  const invalidateRankedTable = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [`relevant-volunteers-${opportunityId}`],
      exact: false,
    });
  }, [opportunityId, queryClient]);

  const resolveSelectedAssociations = useCallback(
    async (
      allSelected: boolean,
      whereToUpdate: Parameters<
        ActionButton<Association>["onClick"]
      >[0]["whereToUpdate"],
      select: { id?: true; userId?: true; },
    ) => {
      if (allSelected) {
        const userIds = await fetchAllRankedUserIds(
          opportunityId,
          isRemoteOpportunity,
          rankedTableFilters,
        );
        if (userIds.length === 0) return [];
        return associationRepo.find({
          where: Association.hyperFilter({
            organizationId: currOrganization!.id,
            userId: { $in: userIds },
            roles: {
              $some: OrganizationRoles.Volunteer,
            },
            user: { $is: { isDeleted: false } },
          }),
          select,
        });
      }
      return associationRepo.find({
        where: whereToUpdate,
        select,
      });
    },
    [currOrganization, opportunityId, isRemoteOpportunity, rankedTableFilters],
  );

  const volunteerFilters = useMemo(() => {
    if (!currOrganization) return [];
    return getFilters(currOrganization).filter(
      (filter) => filter.id !== "relevantVolunteers",
    );
  }, [currOrganization]);

  const sendMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      linko.opportunity.mutations.sendRelevantVolunteersSms({
        opportunityId,
        userIds,
      }),
  });

  const sendWhatsapp = async (
    templateKey: string,
    customFields: Record<string, string> | undefined,
    confirmedRecentUserIds: string[],
  ) => {
    setWhatsappSending(true);
    try {
      const { data: result } =
        await linko.opportunity.mutations.sendRelevantVolunteersWhatsapp({
          opportunityId,
          userIds: whatsappRecipients.map((recipient) => recipient.userId),
          templateKey,
          customFields,
          confirmedRecentUserIds,
        });
      onSendSuccess?.(result);
      setWhatsappRecipients([]);
    } catch (error: unknown) {
      onSendSuccess?.({
        sent: 0,
        failed: 1,
        message: isAxiosTypedLinkoError(error)
          ? error.linkoError.message
          : "שליחת הוואטסאפ נכשלה",
      });
    } finally {
      setWhatsappSending(false);
    }
  };

  const allVolunteerColumns = useVolunteerColumns(
    currOrganization,
    HyperTable,
    undefined,
    setCalledAssociationId,
  );

  const volunteerColumns = useMemo(() => {
    const hiddenFields = new Set([
      "user.email",
      "teams.team.name",
      "user.skills.skill.name",
      "user.numberID",
      "scholarshipsHoursNumber",
      "preferredPositions",
    ]);

    const visible = allVolunteerColumns.filter(
      (column) => !hiddenFields.has(String(column.field)),
    );

    const activityStatusColumn = visible.find(
      (column) => column.field === "activityStatus",
    );
    const withoutActivityStatus = visible.filter(
      (column) => column.field !== "activityStatus",
    );

    const resized = withoutActivityStatus.map((column) => {
      if (column.field === "communicationRecords") {
        return { ...column, minSize: 120, size: 140 };
      }
      return column;
    });

    return activityStatusColumn
      ? [...resized, activityStatusColumn]
      : resized;
  }, [allVolunteerColumns]);

  const { columnsBeforeDistance, columnsAfterDistance } = useMemo(
    () => splitVolunteerColumnsForDistance(volunteerColumns),
    [volunteerColumns],
  );

  const buttons: ActionButton<Association>[] = useMemo(
    () => [
      {
        icon: <PersonAddAlt1Icon />,
        title: "הוספה למשימה",
        onClick: async ({ whereToUpdate, context }) => {
          const associations = await resolveSelectedAssociations(
            context.allSelected,
            whereToUpdate,
            { id: true },
          );
          try {
            const matchWeight =
              (await resolveMatchWeightForAdd({
                opportunityId,
                defaultMatchWeight: opportunityMeta?.defaultMatchWeight,
                prompt: promptMatchWeight,
              })) ?? 1;
            for (let i = 0; i < associations.length; i++) {
              await addUserToOpportunity({
                opportunityId,
                associationId: associations[i]!.id,
                matchWeight,
                showSuccessAlert: i === associations.length - 1,
              });
            }
          } catch (error) {
            if (error instanceof MatchWeightPromptCancelled) return;
            throw error;
          }
          await invalidateRankedTable();
          onAddSuccess?.();
          return { refetch: "remove" as const, resetSelection: true };
        },
      },
      {
        icon: <SmsIcon />,
        title: "שליחת הודעה",
        onClick: async ({ whereToUpdate, context }) => {
          const associations = await resolveSelectedAssociations(
            context.allSelected,
            whereToUpdate,
            { userId: true },
          );
          const userIds = associations
            .map((a) => a.userId)
            .filter(Boolean) as string[];
          if (userIds.length === 0) return;

          const res = await sendMutation.mutateAsync(userIds);
          const raw = res as unknown;
          const result: { sent: number; failed: number; message: string; } =
            typeof (raw as { data?: unknown; })?.data === "object" &&
              (raw as { data: { sent: number; failed: number; message: string; }; })
                .data != null
              ? (
                raw as {
                  data: { sent: number; failed: number; message: string; };
                }
              ).data
              : (raw as { sent: number; failed: number; message: string; });
          onSendSuccess?.(result);
          await invalidateRankedTable();
          return { refetch: "remove" as const, resetSelection: true };
        },
      },
      {
        icon: (
          <HugeiconsIcon icon={Message01Icon} size={22} strokeWidth={2} />
        ),
        title: "שליחת וואטסאפ",
        onClick: async ({ whereToUpdate, context }) => {
          const associations = await resolveSelectedAssociations(
            context.allSelected,
            whereToUpdate,
            { userId: true },
          );
          const userIds = associations
            .map((a) => a.userId)
            .filter(Boolean) as string[];
          if (userIds.length === 0) {
            onSendSuccess?.({
              sent: 0,
              failed: 1,
              message: "לא נבחרו מתנדבים לשליחה",
            });
            return;
          }
          const cappedUserIds = userIds.slice(0, ADMIN_BULK_ACTION_LIMIT);
          if (userIds.length > ADMIN_BULK_ACTION_LIMIT) {
            setAlertText(
              `יישלח עד ${ADMIN_BULK_ACTION_LIMIT} מתנדבים. חזרו על הפעולה לשאר.`,
            );
            setSeverity("warning");
          }
          const withUser = await associationRepo.find({
            where: Association.hyperFilter({
              organizationId: currOrganization!.id,
              userId: { $in: cappedUserIds },
            }),
            include: { user: true },
          });
          const seen = new Set<string>();
          const recipients: WhatsappDialogRecipient[] = [];
          for (const row of withUser) {
            if (!row.userId || seen.has(row.userId)) continue;
            seen.add(row.userId);
            recipients.push({
              userId: row.userId,
              name: row.user?.name,
              firstName: row.user?.firstName,
              phoneNumber: row.user?.phoneNumber,
            });
          }
          setWhatsappRecipients(recipients);
          return { refetch: false, resetSelection: false };
        },
      },
      {
        icon: <TaskAltIcon />,
        title: "חיפוש משימות מתאימות",
        onClick: async ({ whereToUpdate }) => {
          const associations = await associationRepo.find({
            where: whereToUpdate,
            include: { user: true },
            limit: 2,
          });
          if (associations.length !== 1) return;
          const association = associations[0]!;
          setOpportunitiesDialogVolunteer({
            userId: association.userId,
            userName: association.user?.name ?? "",
            associationId: association.id,
            hasVolunteerCoordinates: hasUsableVolunteerCoordinates(
              association.user?.coordinates,
            ),
          });
        },
      },
    ],
    [
      addUserToOpportunity,
      promptMatchWeight,
      opportunityId,
      opportunityMeta?.defaultMatchWeight,
      onAddSuccess,
      onSendSuccess,
      invalidateRankedTable,
      resolveSelectedAssociations,
      sendMutation,
      currOrganization,
    ],
  );
  const visibleButtons = isCallCenterStation
    ? buttons.filter((button) => button.title !== "שליחת וואטסאפ")
    : buttons;

  const openInNewTabAction = useMemo(
    () =>
      standalone
        ? []
        : [
          {
            label: "פתיחה בטאב חדש (מתנדבים בלבד)",
            icon: <OpenInNewIcon fontSize="small" />,
            onClick: () => {
              const url = `/events/${opportunityId}?selectedList=relevant-volunteers&view=relevant-volunteers-only`;
              window.open(url, "_blank", "noopener,noreferrer");
            },
          },
        ],
    [opportunityId, standalone],
  );

  if (!previewRows && (!currOrganization || !isOpportunityMetaFetched)) {
    return (
      <Stack width="100%" alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <HyperTable
        tableId={`relevant-volunteers-${opportunityId}`}
        title={null}
        findConfig={{
          enabled: previewRows ? true : !!opportunityId && filtersSynced,
          limit: PAGE_SIZE,
          where: baseAssociationWhere,
          include: ASSOCIATION_INCLUDE,
        }}
        queryOptions={
          previewRows
            ? {
                dataFn: async () => previewRows,
                countFn: async () => previewRows.length,
                deps: [previewRows.map((row) => row.id).join(",")],
              }
            : {
                dataFn: rankedDataFn,
                countFn: rankedCountFn,
                deps: [
                  opportunityId,
                  isRemoteOpportunity,
                  currOrganization?.id,
                  rankedFiltersKey,
                ],
              }
        }
        onFiltersChange={handleFiltersChange}
        onRowClick={
          previewRows ? undefined : (_, row) => setCardAssociation(row.original)
        }
        getRowClassName={(row) =>
          row.original.id === calledAssociationId
            ? "table-row--called"
            : undefined
        }
      >
        <HyperTable.Toolbar moreActions={openInNewTabAction}>
          <HyperTable.SelectColumn selectAllBehavior="cross-page" />
          {volunteerFilters.map((filter) =>
            isEntityFilter(filter) ? (
              <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
            ) : (
              <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
            ),
          )}
        </HyperTable.Toolbar>
        {columnsBeforeDistance.map((column) => (
          <HyperTable.Column key={column.field.toString()} {...column} />
        ))}
        <RelevantVolunteersDistanceColumn
          distanceByUserId={distanceByUserId}
          isRemoteOpportunity={isRemoteOpportunity}
        />
        {columnsAfterDistance.map((column) => (
          <HyperTable.Column key={column.field.toString()} {...column} />
        ))}
        <HyperTable.ActionsBar buttons={visibleButtons} />
        <SendEventParticipantsWhatsappDialog
          open={whatsappRecipients.length > 0}
          onClose={() => {
            if (!whatsappSending) setWhatsappRecipients([]);
          }}
          sending={whatsappSending}
          recipientCount={whatsappRecipients.length}
          recipients={whatsappRecipients}
          audience="relevant"
          opportunityId={opportunityId}
          opportunity={
            opportunityMeta
              ? {
                id: opportunityId,
                title: opportunityMeta.title,
                location: opportunityMeta.location,
                hasTime: opportunityMeta.hasTime,
                startDate: opportunityMeta.startDate,
                timeDescription: opportunityMeta.timeDescription,
              }
              : undefined
          }
          onSend={sendWhatsapp}
        />
        <UserCardPopup
          userData={cardAssociation}
          setUserData={setCardAssociation}
        />
      </HyperTable>
      {opportunitiesDialogVolunteer && currOrganization?.id && (
        <RelevantOpportunitiesForVolunteerDialog
          open={!!opportunitiesDialogVolunteer}
          onClose={() => setOpportunitiesDialogVolunteer(null)}
          userId={opportunitiesDialogVolunteer.userId}
          userName={opportunitiesDialogVolunteer.userName}
          associationId={opportunitiesDialogVolunteer.associationId}
          organizationId={currOrganization?.id ?? ""}
          excludeOpportunityId={opportunityId}
          hasVolunteerCoordinates={
            opportunitiesDialogVolunteer.hasVolunteerCoordinates
          }
        />
      )}
    </>
  );
}
